import { gql } from "lib/apolloClient";
import { jobFeedFragment, subTaskFeedFragment } from "./IntegrationsFragments";

export const getOrganizationCurriculumProgramsQuery = gql`
  query getOrganizationCurriculumPrograms($organizationId: ID!) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        curriculumPrograms {
          id
          label
          acronym
          curriculum {
            id
            label
            type
          }
        }
      }
    }
  }
`;

export const getIntegrationsDashboardInfoQuery = gql`
  query GetIntegrationCurriculumServiceDetails(
    $input: IntegrationCurriculumServiceDetailsInput!
  ) {
    integration {
      getIntegrationCurriculumServiceDetails(input: $input) {
        id
        curriculumProgramId
        service {
          id
          name
          features {
            name
            hasCompletedJob
            setupInfo {
              configStatus
              isSetupEnabled
            }
            lastJob {
              type
              createdAt
              status
              id
              type
              createdAt
              status
              serviceName
              isLatest
              updatedAt
              academicYear {
                id
              }
              subTasksFeed {
                totalCount
                errorCount
              }
              curriculum
              serviceName
              metadata
            }
            settings
          }
        }
      }
    }
  }
`;

export const getIntegrationsProcessInfoQuery = gql`
  query GetIntegrationCurriculumServiceDetails(
    $input: IntegrationCurriculumServiceDetailsInput!
  ) {
    integration {
      getIntegrationCurriculumServiceDetails(input: $input) {
        id
        curriculumProgramId
        service {
          id
          name
          features {
            name
            setupInfo {
              configStatus
              isSetupEnabled
            }
            lastJob {
              id
              type
              createdAt
              status
              serviceName
              isLatest
              updatedAt
              academicYear {
                id
              }
              curriculum
              serviceName
            }
            settings
          }
        }
      }
    }
  }
`;

export const getIntegrationsActiveFeaturesQuery = gql`
  query GetIntegrationCurriculumServiceDetails(
    $input: IntegrationCurriculumServiceDetailsInput!
  ) {
    integration {
      getIntegrationCurriculumServiceDetails(input: $input) {
        id
        curriculumProgramId
        service {
          name
          features {
            name
            settings
            setupInfo {
              configStatus
              isSetupEnabled
            }
          }
        }
      }
    }
  }
`;

export const getJobDetailsQuery = gql`
  query getJobDetails(
    $id: ID!
    $subTaskFilters: IntegrationSubTaskFeedFilterInput
  ) {
    node(id: $id, type: INTEGRATION_JOB) {
      id
      ... on IntegrationJob {
        type
        status
        serviceName
        createdAt
        curriculum
        curriculumId
        type
        metadata
        subTasksFeed(filters: $subTaskFilters) {
          totalCount
          errorCount
        }
      }
    }
  }
`;

export const getSyncStatusQuery = gql`
  query getSyncStatus($id: ID!, $type: [JOB_TYPE_ENUM!]) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        syncStatus(type: $type) {
          id
          status
          curriculumProgramId
          acronym
          jobType
        }
      }
    }
  }
`;

export const getJobFeedQuery = gql`
  query getJobFeed(
    $id: ID!
    $afterV2: String
    $filters: OrganizationIntegrationJobFilterInput
    $academicYearState: ACADEMIC_YEAR_STATE_ENUM
    $first: Int
    $curriculumProgramId: ID
    $subTaskFilters: IntegrationSubTaskFeedFilterInput
    $fetchSubTasksCount: Boolean! = false
    $fetchSubtaskEdges: Boolean! = false
    $fetchWorkflowProgress: Boolean! = false
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationCourseFeedEdgeItem
      }
    }
  }
  ${jobFeedFragment.organizationJobFeedEdge}
`;

export const subTaskFeedQuery = gql`
  query getSubTaskFeed($id: ID!, $filters: IntegrationSubTaskFeedFilterInput) {
    node(id: $id, type: INTEGRATION_JOB) {
      id
      ... on IntegrationJob {
        ...subTaskFeedEdgeItem
      }
    }
  }
  ${subTaskFeedFragment.subTaskFeedEdge}
`;

export const getAvailableServicesQuery = gql`
  query getAvailableServices(
    $id: ID!
    $filters: AvailableSyncServicesV2Filters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        availableSyncServicesV2(filters: $filters)
      }
    }
  }
`;

export const getThirdPartyMappingV2Query = gql`
  query thirdPartyMappingsV2(
    $id: ID!
    $first: Int
    $after: String
    $filters: ThirdPartyMappingV2Filters!
    $orderBy: THIRD_PARTY_MAPPING_ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        thirdPartyMappingsV2(
          first: $first
          after: $after
          filters: $filters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          totalCount
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          edges {
            node {
              toddleEntity
              thirdPartyEntity
              fuzzyMappedEntity
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationAcademicYearTermsQuery = gql`
  query getOrganizationAcademicYearTerms(
    $organizationId: ID!
    $termFilters: TermFilters
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        academicYears {
          id
          startDate
          endDate
          terms(filters: $termFilters) {
            id
            label
            startDate
            endDate
          }
        }
      }
    }
  }
`;

export const getOrganizationCourseFeedQuery = gql`
  query getOrganizationCourseFeed(
    $first: Int = 10
    $after: String
    $filters: CourseFilterInput
    $courseGradeFilters: CourseGradeFilters
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $curriculumWiseCountFilters: CurriculumWiseCountFilters
  ) {
    platform {
      organization {
        id
        courses(
          first: $first
          after: $after
          filters: $filters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          pageInfo {
            endCursor
            hasNextPage
          }
          totalCount
          curriculumWiseCount(filters: $curriculumWiseCountFilters) {
            curriculumProgram {
              id
            }
            count
          }
          edges {
            node {
              id
              title
              profileImage
              grades(filters: $courseGradeFilters) {
                id
                name
              }
              isArchived
            }
          }
        }
      }
    }
  }
`;
